/**
 * Service-worker → page message channel subscription.
 *
 * Both products receive SW relay messages (silent `TOKEN_REFRESH`
 * re-registration, foreground push forwarding, notification clicks) through
 * `navigator.serviceWorker`'s `message` event. This hook owns the
 * subscribe/teardown plumbing; the HANDLER stays app-side — message types,
 * identity gates, foreground rendering, and notificationclick routes are
 * per-app parameterization by design.
 */
import { useEffect } from 'react';

export interface UseServiceWorkerMessagesOptions {
  /** Subscribe only while true (typically: authenticated and not kiosk). */
  enabled: boolean;
  /**
   * App-side message handler. Memoize it (`useCallback`) — an identity
   * change re-subscribes.
   */
  onMessage: (event: MessageEvent) => void;
}

export function useServiceWorkerMessages(
  options: UseServiceWorkerMessagesOptions,
): void {
  const { enabled, onMessage } = options;

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [enabled, onMessage]);
}
